import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { parseDate, formatRange, eventStatus, eventColor, EVENT_TYPES } from '../lib/format.js';
import { baueModell, anteil, kurzDatum } from '../lib/timeline.js';

const KOPF_HOEHE = 30;   // Band mit den Datumslabels
const SPUR_HOEHE = 38;   // Zeilenhöhe einer Balkenspur
const BALKEN_HOEHE = 26;
const PUNKT_HOEHE = 30;  // Zeile für die kurzen Termine

// Alles unter dieser Breite ist als Balken nicht mehr lesbar: der Titel passt
// nicht hinein, und übrig bliebe ein Farbklecks ohne Aussage. Solche Termine
// werden stattdessen als Punkt in einer eigenen Zeile gezeigt.
const PUNKT_SCHWELLE = 2.5;

// Marken (R/P) brauchen Platz. Auf schmalen Balken verdrängen sie den Titel.
const MARKEN_SCHWELLE = 14;

/**
 * Zeitachse über alles mit Anfang und Ende — Events wie Raid-Fenster.
 *
 * Der Zeitraum schliesst den aktuellen Zeitpunkt immer mit ein, sonst wäre die
 * Jetzt-Linie bei rein zukünftigen Einträgen ausserhalb des Bildes.
 */
export default function Timeline({ events, linkFor, marken, titel = 'Zeitachse' }) {
  const [jetzt, setJetzt] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setJetzt(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const zielFuer = linkFor ?? ((event) => `/event/${event.slug}`);
  const modell = useMemo(() => baueModell(events, jetzt), [events, jetzt]);

  // Lange und kurze Termine trennen — sie brauchen verschiedene Darstellungen.
  const geteilt = useMemo(() => {
    if (!modell) return null;

    const breite = (e) =>
      anteil(parseDate(e.endsAt), modell.von, modell.spanne)
      - anteil(parseDate(e.startsAt), modell.von, modell.spanne);

    const lang = events.filter((e) => breite(e) >= PUNKT_SCHWELLE);
    const kurz = events.filter((e) => breite(e) < PUNKT_SCHWELLE);

    return { lang, kurz, breite };
  }, [modell, events]);

  if (!modell || !geteilt) return null;

  // Spuren nur für die Balken neu berechnen; die Punkte teilen sich eine Zeile.
  const balkenModell = geteilt.lang.length > 0 ? baueModell(geteilt.lang, jetzt) : null;
  const spuren = balkenModell ? balkenModell.spuren : [];

  const { von, bis, spanne, ticks, jetztAnteil } = modell;
  const pos = (datum) => anteil(datum, von, spanne);

  const balkenHoehe = spuren.length * SPUR_HOEHE;
  const punktHoehe = geteilt.kurz.length > 0 ? PUNKT_HOEHE : 0;

  return (
    <section className="timeline panel" aria-label="Zeitachse">
      <div className="timeline__head">
        <h2>{titel}</h2>
        <span className="timeline__range">{kurzDatum(von)} – {kurzDatum(bis)}</span>
      </div>

      <div className="timeline__scroll">
        <div className="timeline__axis" style={{ height: KOPF_HOEHE + balkenHoehe + punktHoehe }}>

          <div className="timeline__labels" style={{ height: KOPF_HOEHE }}>
            {ticks.map((tick) => {
              const p = pos(tick);
              // Ein Label direkt neben der Jetzt-Linie wäre unlesbar. Die
              // Rasterlinie bleibt, das Label entfällt.
              if (jetztAnteil !== null && Math.abs(p - jetztAnteil) < 9) return null;

              return (
                <span
                  key={tick.getTime()}
                  className="timeline__tick"
                  style={{ left: `${p}%`, transform: randAusrichtung(p) }}
                >
                  {kurzDatum(tick)}
                </span>
              );
            })}

            {jetztAnteil !== null && (
              <span
                className="timeline__now-label"
                style={{ left: `${jetztAnteil}%`, transform: randAusrichtung(jetztAnteil) }}
              >
                Jetzt
              </span>
            )}
          </div>

          <div className="timeline__body" style={{ height: balkenHoehe + punktHoehe }}>
            {spuren.map((_, i) => (
              <div key={i} className="timeline__lane" style={{ top: i * SPUR_HOEHE, height: SPUR_HOEHE }} />
            ))}

            {geteilt.kurz.length > 0 && (
              <div className="timeline__lane timeline__lane--punkte"
                   style={{ top: balkenHoehe, height: PUNKT_HOEHE }} />
            )}

            {ticks.map((tick) => (
              <div key={tick.getTime()} className="timeline__grid" style={{ left: `${pos(tick)}%` }} />
            ))}

            {jetztAnteil !== null && (
              <div className="timeline__now" style={{ left: `${jetztAnteil}%` }} />
            )}

            {spuren.map((spur, spurIndex) =>
              spur.map((event) => {
                const status = eventStatus(event, jetzt);
                const links = pos(parseDate(event.startsAt));
                const breite = pos(parseDate(event.endsAt)) - links;

                return (
                  <Link
                    key={event.id}
                    to={zielFuer(event)}
                    className={`timeline__bar timeline__bar--${status}`}
                    style={{
                      left: `${links}%`,
                      width: `${breite}%`,
                      top: spurIndex * SPUR_HOEHE + (SPUR_HOEHE - BALKEN_HOEHE) / 2,
                      height: BALKEN_HOEHE,
                      '--bar-accent': eventColor(event),
                    }}
                    title={tooltip(event)}
                  >
                    <span className="timeline__bar-text">{event.title}</span>

                    {breite >= MARKEN_SCHWELLE && marken?.(event)?.map((m) => (
                      <span
                        className="timeline__marke"
                        key={m.kurz}
                        title={m.titel}
                        style={{ '--marke-farbe': m.farbe }}
                      >
                        {m.kurz}
                      </span>
                    ))}
                  </Link>
                );
              })
            )}

            {/* Kurze Termine als Punkte. Der Titel steht im Tooltip — auf der
                Achse wäre für ihn ohnehin kein Platz. */}
            {geteilt.kurz.map((event) => {
              const status = eventStatus(event, jetzt);

              return (
                <Link
                  key={event.id}
                  to={zielFuer(event)}
                  className={`timeline__punkt timeline__punkt--${status}`}
                  style={{
                    left: `${pos(parseDate(event.startsAt))}%`,
                    top: balkenHoehe + PUNKT_HOEHE / 2,
                    '--bar-accent': eventColor(event),
                  }}
                  title={tooltip(event)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {geteilt.kurz.length > 0 && (
        <p className="timeline__fuss">
          Punkte in der unteren Zeile sind kurze Termine — Raid- und
          Rampenlicht-Stunden von je einer Stunde.
        </p>
      )}
    </section>
  );
}

const tooltip = (event) =>
  `${event.title}\n${EVENT_TYPES[event.eventType] ?? ''}\n${formatRange(event.startsAt, event.endsAt)}`;

/**
 * Labels sind normalerweise auf ihrer Position zentriert. Am äusseren Rand
 * würde die Hälfte abgeschnitten, dort wird deshalb bündig ausgerichtet.
 */
function randAusrichtung(prozent) {
  if (prozent < 5) return 'translateX(0)';
  if (prozent > 95) return 'translateX(-100%)';
  return 'translateX(-50%)';
}
